import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, UserRole, LoginResponse } from '../models/user.model';
import { LaboratoryType } from '../models/laboratory.model';
import CryptoJS from 'crypto-js';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken?: string;
}

export interface AuthCredentials {
  email: string;
  password: string;
}

const STORAGE_KEY = 'auth_tokens';
const USER_KEY = 'auth_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = `${environment.api.baseUrl}/auth`;
  private refreshInFlight$: Observable<string | null> | null = null;
  private readonly userState = signal<User | null>(this.getStoredUser());
  // Signal pour déclencher la réactivité du computed isAuthenticated
  private readonly tokenState = signal<number>(0);

  /**
   * Vérifie l'authentification en temps réel en validant le token JWT
   * Utilise un computed pour une réactivité automatique
   * Le tokenState signal garantit que le computed se met à jour quand les tokens changent
   */
  readonly isAuthenticated = computed(() => {
    // Lire le signal pour déclencher la réactivité
    this.tokenState();
    const token = this.getAccessToken();
    return token !== null && this.isTokenValid(token);
  });
  readonly currentUser = computed(() => this.userState());
  readonly isSuperAdmin = computed(() => this.userState()?.role === UserRole.SUPER_ADMIN);
  readonly isExclusive = computed(
    () => this.userState()?.labType === LaboratoryType.LABORATOIRE_PRELEVEUR,
  );
  readonly isExecutor = computed(
    () => this.userState()?.labType === LaboratoryType.LABORATOIRE_EXECUTANT,
  );
  readonly isCommercial = computed(() => this.userState()?.role === UserRole.COMMERCIAL);

  constructor(private readonly http: HttpClient) {
    // Vérifier l'état d'authentification au démarrage et nettoyer si le token est expiré
    this.checkAuthState();
  }

  // ── Tokens ──────────────────────────────────────────────

  getAccessToken(): string | null {
    return this.getTokens()?.accessToken ?? null;
  }

  getRefreshToken(): string | null {
    return this.getTokens()?.refreshToken ?? null;
  }

  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    // Mettre à jour le signal pour déclencher la réactivité du computed isAuthenticated
    this.tokenState.update((v) => v + 1);
  }

  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(USER_KEY);
    this.userState.set(null);
    // Mettre à jour le signal pour déclencher la réactivité du computed isAuthenticated
    this.tokenState.update((v) => v + 1);
  }

  // ── User ────────────────────────────────────────────────

  private setUser(user: User): void {
    localStorage.setItem(USER_KEY, this.encrypt(user));
    this.userState.set(user);
  }

  private getStoredUser(): User | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      if (!raw) return null;
      return this.decrypt<User>(raw);
    } catch {
      return null;
    }
  }

  // ── Login ───────────────────────────────────────────────

  login(dto: AuthCredentials): Observable<LoginResponse> {
    const url = `${this.baseUrl}/login`;
    return this.http.post<LoginResponse>(url, dto).pipe(
      tap((res) => {
        this.setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
        this.setUser(res.user);
      }),
    );
  }

  // ── Logout ──────────────────────────────────────────────

  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    const url = `${this.baseUrl}/logout`;
    return this.http.post<void>(url, { refreshToken }).pipe(
      catchError(() => of(void 0)),
      tap(() => this.clearTokens()),
    );
  }

  // ── Fetch current user profile ──────────────────────────

  fetchMe(): Observable<User | null> {
    if (!this.getAccessToken()) {
      return of(null);
    }
    const url = `${this.baseUrl}/me`;
    return this.http.get<User>(url).pipe(
      tap((user) => this.setUser(user)),
      catchError(() => {
        this.clearTokens();
        return of(null);
      }),
    );
  }

  // ── Refresh ─────────────────────────────────────────────

  refreshAccessToken(): Observable<string | null> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return of(null);
    }

    const url = `${this.baseUrl}/refresh`;
    this.refreshInFlight$ = this.http.post<RefreshResponse>(url, { refreshToken }).pipe(
      map((res) => {
        const accessToken = res.accessToken;
        const nextRefresh = res.refreshToken ?? refreshToken;
        this.setTokens({ accessToken, refreshToken: nextRefresh });
        return accessToken;
      }),
      catchError(() => {
        this.clearTokens();
        return of(null);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.refreshInFlight$;
  }

  // ── Token Validation ────────────────────────────────────

  /**
   * Vérifie si le token JWT est valide (existe et n'est pas expiré)
   */
  isTokenValid(token: string | null): boolean {
    if (!token) return false;

    try {
      const payload = this.decodeJwtPayload(token);
      if (!payload || !payload.exp) return false;

      // Vérifier l'expiration (exp est en secondes depuis epoch)
      const expirationTime = payload.exp * 1000; // Convertir en millisecondes
      const now = Date.now();

      // Ajouter une marge de sécurité de 5 secondes pour éviter les problèmes de timing
      return expirationTime > now + 5000;
    } catch {
      return false;
    }
  }

  /**
   * Décode le payload d'un JWT sans vérifier la signature
   * Format JWT: header.payload.signature
   */
  private decodeJwtPayload(token: string): { exp?: number; [key: string]: unknown } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Décoder le payload (partie 2)
      const payload = parts[1];
      // Remplacer les caractères base64url par base64 standard
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // Ajouter le padding si nécessaire
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const decoded = atob(padded);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Vérifie l'état d'authentification et nettoie si le token est expiré
   * Méthode publique pour permettre l'initialisation depuis AppComponent
   */
  checkAuthState(): void {
    const token = this.getAccessToken();
    if (token && !this.isTokenValid(token)) {
      // Token expiré, nettoyer le localStorage
      // Le computed isAuthenticated se mettra à jour automatiquement après clearTokens()
      this.clearTokens();
    }
    // Si le token est valide, le computed isAuthenticated retournera true automatiquement
  }

  // ── Private ─────────────────────────────────────────────

  private getTokens(): AuthTokens | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthTokens) : null;
    } catch {
      return null;
    }
  }

  private encrypt(data: unknown): string {
    return CryptoJS.AES.encrypt(JSON.stringify(data), 'biomaf_2026').toString();
  }

  private decrypt<T>(cipher: string): T | null {
    try {
      const bytes = CryptoJS.AES.decrypt(cipher, 'biomaf_2026');
      const decoded = bytes.toString(CryptoJS.enc.Utf8);
      if (!decoded) return null;
      return JSON.parse(decoded) as T;
    } catch {
      return null;
    }
  }

  public forgetPassword(username: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/forget-password`, { username });
  }

  public resetPassword(token: string, newPassword: string): Observable<{ done: boolean }> {
    return this.http.post<{ done: boolean }>(`${this.baseUrl}/reset-password`, {
      token,
      newPassword,
    });
  }
}
